import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useIntl } from 'react-intl';
import { Select, Skeleton } from 'antd';
import { get, groupBy } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBoxOpen,
  faBuilding,
  faCar,
  faCircleExclamation,
  faFileLines,
  faHotel,
  faMagnifyingGlass,
  faPersonHiking,
  faQuoteLeft,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';

import { IQuote } from '@share/common-types';
import { getQuotes, IQuoteState, markQuotesAsRead } from '@share/store/slices';
import { FunctionalRootState, getAllPackageQuotes, getKeyQuotesFromPath, isQuotesFromPath, RootState } from '@share/utils';

import { QuotePackageModalCompare } from '../../../common/quote-package-modal-compare';
import { parseApiDate } from '../../utils';
import { AccountEmptyState } from '../empty-state';
// Same status / product / sort controls as My bookings and certificates.
import filterStyles from '../bookings/styles.module.scss';

import styles from './styles.module.scss';

type Product = 'hotel' | 'condo' | 'car' | 'activity' | 'package';
type StatusKey = 'active' | 'expired' | 'all';
type ProductKey = '' | Product;
type SortKey = 'newest' | 'soonest';

const PRODUCT_ICONS: Record<Product, IconDefinition> = {
  hotel: faHotel,
  condo: faBuilding,
  car: faCar,
  activity: faPersonHiking,
  package: faBoxOpen,
};

const PRODUCT_LABELS: Record<Product, string> = {
  hotel: 'my.account.hub.quotes.type.hotel',
  condo: 'my.account.hub.quotes.type.condo',
  car: 'my.account.hub.quotes.type.car',
  activity: 'my.account.hub.quotes.type.activity',
  package: 'my.account.hub.quotes.type.package',
};

interface IQuoteCard {
  id: string;
  product: Product;
  options: IQuote[];
  image: string | null;
  title: string;
  places: string[];
  start: Date | null;
  end: Date | null;
  fromPrice: number | null;
  publicPrice: number | null;
  currency: string;
  savePercent: number;
  comment: string;
  created: Date | null;
  isExpired: boolean;
  packageCounts: { hotels: number; condos: number; cars: number };
}

// Cars carry their dates as pickUpDate / dropOffDate; everything else uses checkIn / checkOut.
const getStart = (quote: IQuote): Date | null => parseApiDate(get(quote, 'pickUpDate') || quote.checkIn || get(quote, 'availableDates[0].from'));
const getEnd = (quote: IQuote): Date | null => parseApiDate(get(quote, 'dropOffDate') || quote.checkOut || get(quote, 'availableDates[0].to'));

const unique = (values: (string | undefined)[]): string[] => Array.from(new Set(values.filter((value): value is string => !!value)));

const toCard = (id: string, product: Product, head: IQuote, options: IQuote[]): IQuoteCard => {
  const starts = options.map(getStart).filter((date): date is Date => !!date);
  const ends = options.map(getEnd).filter((date): date is Date => !!date);
  const priced = options.filter(option => (option.price || 0) > 0).sort((a, b) => a.price - b.price);
  const cheapest = priced[0];
  const publicPrice = cheapest?.publicPrice && cheapest.publicPrice > cheapest.price ? cheapest.publicPrice : null;
  const pq = head.packageQuotes;

  return {
    id,
    product,
    options,
    image: options.find(option => !!option.image)?.image || null,
    title: options.length === 1
      ? (options[0].displayName || get(options[0], 'hotelName') || get(options[0], 'condoName') || get(options[0], 'carType') || '')
      : '',
    places: unique(options.map(option => option.place || get(option, 'pickUpLocationDetail'))),
    start: starts.length ? new Date(Math.min(...starts.map(date => date.getTime()))) : null,
    end: ends.length ? new Date(Math.max(...ends.map(date => date.getTime()))) : null,
    fromPrice: cheapest ? cheapest.price : null,
    publicPrice,
    currency: cheapest?.currency || head.currency || 'USD',
    savePercent: publicPrice && cheapest ? Math.round(((publicPrice - cheapest.price) / publicPrice) * 100) : 0,
    comment: (head.comment || '').trim(),
    created: head.creationDate ? new Date(head.creationDate) : null,
    isExpired: !!head.isExpired,
    packageCounts: {
      hotels: pq?.hotelQuotes?.length || 0,
      condos: pq?.condoQuotes?.length || 0,
      cars: pq?.carQuotes?.length || 0,
    },
  };
};

/** Quotes shared with the member, one card per quote; details and reserve open the existing compare modal. */
export const AccountQuotes = (): React.ReactElement => {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch<ThunkDispatch<RootState, undefined, Action>>();

  const loginLoading = useSelector((state: FunctionalRootState) => state.loginStore.loading);
  const account = useSelector((state: FunctionalRootState) => state.loginStore.account);
  const items = useSelector((state: FunctionalRootState) => state.navigationMenuStore.items);

  // Same rule as the legacy quotes page: car quotes only where cars share/compare is enabled.
  const isCarsEnabled = useMemo(() => {
    if (!account?.carsShareCompareEnabled) {
      return false;
    }
    if (!account.isRSITemplate) {
      return true;
    }
    const siteIds = account.carsShareCompareRsiSiteidList || [];
    const orgIds = account.carsShareCompareRsiOrgidList || [];
    return (!siteIds.length && !orgIds.length)
      || siteIds.includes(items?.siteId?.toString() || '')
      || orgIds.includes(items?.organizationId?.toString() || '');
  }, [account, items]);
  const quoteStore: IQuoteState = useSelector((state: FunctionalRootState) => state.quoteStore);
  const { loading, result, errorHotels } = quoteStore;

  const [status, setStatus] = useState<StatusKey>('active');
  const [product, setProduct] = useState<ProductKey>('');
  const [sort, setSort] = useState<SortKey>('newest');
  const [openQuote, setOpenQuote] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Same loading as the legacy page: a quote id in the URL (/account/quotes/:id) opens that quote.
  useEffect(() => {
    if (loginLoading) {
      return;
    }
    const param = reloadKey ? null : getKeyQuotesFromPath();
    dispatch(getQuotes(param));
    if (param && isQuotesFromPath(history)) {
      setOpenQuote(param);
      dispatch(markQuotesAsRead(param));
    }
  }, [loginLoading, reloadKey]);

  const cards = useMemo(() => {
    const byId = (list: IQuote[], type: Product) =>
      Object.entries(groupBy(list, quote => quote.quoteBaseId)).map(([id, group]) => toCard(id, type, group[0], group));
    const packages = Object.entries(groupBy(result.quotesPackages, quote => quote.quoteBaseId))
      .map(([id, group]) => toCard(id, 'package', group[0], group.flatMap(item => getAllPackageQuotes(item))));

    return [
      ...byId(result.quotesHotels, 'hotel'),
      ...byId(result.quotesCondos, 'condo'),
      ...(isCarsEnabled ? byId(result.quotesCars, 'car') : []),
      ...byId(result.quotesActivities, 'activity'),
      ...packages,
    ];
  }, [result, isCarsEnabled]);

  const shown = useMemo(() => cards
    .filter(card => status === 'all' || (status === 'expired' ? card.isExpired : !card.isExpired))
    .filter(card => !product || card.product === product)
    .sort((a, b) => (sort === 'soonest'
      ? (a.start?.getTime() ?? Infinity) - (b.start?.getTime() ?? Infinity)
      : (b.created?.getTime() ?? 0) - (a.created?.getTime() ?? 0))), [cards, status, product, sort]);

  const products: { key: ProductKey; labelId: string }[] = [
    { key: '', labelId: 'my.account.hub.bookings.product.all' },
    { key: 'hotel', labelId: 'my.account.hub.bookings.product.hotels' },
    { key: 'condo', labelId: 'my.account.hub.bookings.product.condos' },
    ...(isCarsEnabled ? [{ key: 'car' as ProductKey, labelId: 'my.account.hub.bookings.product.cars' }] : []),
    { key: 'activity', labelId: 'my.account.hub.bookings.product.activities' },
    { key: 'package', labelId: 'my.account.hub.quotes.product.packages' },
  ];

  const formatDate = (date: Date | null, withYear = true) =>
    date ? intl.formatDate(date, withYear ? { day: '2-digit', month: 'short', year: 'numeric' } : { day: '2-digit', month: 'short' }) : '';

  const formatMoney = (value: number, currency: string) => {
    try {
      return intl.formatNumber(value, { style: 'currency', currency });
    } catch (error: unknown) {
      // An unknown currency code throws; show the code with the amount rather than nothing.
      console.error('Account hub: unknown quote currency', currency, error);
      return `${currency} ${value.toFixed(2)}`;
    }
  };

  const getTitle = (card: IQuoteCard) => {
    if (card.product === 'package') {
      const { hotels, condos, cars } = card.packageCounts;
      const parts = [
        hotels ? intl.formatMessage({ id: 'my.account.hub.quotes.count_hotels' }, { count: hotels }) : '',
        condos ? intl.formatMessage({ id: 'my.account.hub.quotes.count_condos' }, { count: condos }) : '',
        cars ? intl.formatMessage({ id: 'my.account.hub.quotes.count_cars' }, { count: cars }) : '',
      ].filter(Boolean);
      return intl.formatMessage({ id: 'my.account.hub.quotes.package_title' }, { parts: parts.join(' + ') });
    }
    return card.title || intl.formatMessage({ id: 'my.account.hub.quotes.options_title' }, {
      count: card.options.length,
      type: intl.formatMessage({ id: PRODUCT_LABELS[card.product] }).toLowerCase(),
    });
  };

  const getPlaces = (card: IQuoteCard) => {
    if (card.places.length <= 2) {
      return card.places.join(', ');
    }
    return intl.formatMessage({ id: 'my.account.hub.quotes.places_more' }, { places: card.places.slice(0, 2).join(', '), count: card.places.length - 2 });
  };

  const handleView = (card: IQuoteCard) => {
    setOpenQuote(card.id);
    dispatch(markQuotesAsRead(card.id));
  };

  const renderList = () => {
    if (loading) {
      return (
        <ul className={filterStyles.list}>
          {[0, 1, 2].map(key => (
            <li key={key} className={filterStyles.card}>
              <div className={filterStyles.media} />
              <div className={filterStyles.body}><Skeleton active paragraph={{ rows: 3 }} /></div>
            </li>
          ))}
        </ul>
      );
    }
    if (errorHotels) {
      return (
        <AccountEmptyState
          variant="panel"
          icon={faCircleExclamation}
          title={intl.formatMessage({ id: 'my.account.hub.quotes.error_title' })}
          body={intl.formatMessage({ id: 'my.account.hub.bookings.error_body' })}
          action={{ label: intl.formatMessage({ id: 'my.account.hub.bookings.retry' }), onClick: () => setReloadKey(key => key + 1) }}
        />
      );
    }
    if (!cards.length) {
      return (
        <AccountEmptyState
          variant="panel"
          icon={faFileLines}
          title={intl.formatMessage({ id: 'my.account.hub.quotes.empty_title' })}
          body={intl.formatMessage({ id: 'my.account.hub.quotes.empty_body' })}
        />
      );
    }
    if (!shown.length) {
      return (
        <AccountEmptyState
          variant="panel"
          icon={faMagnifyingGlass}
          title={intl.formatMessage({ id: 'my.account.hub.quotes.no_match_title' })}
          action={product ? { label: intl.formatMessage({ id: 'my.account.hub.bookings.clear_filters' }), onClick: () => setProduct('') } : undefined}
        />
      );
    }

    return (
      <ul className={filterStyles.list}>
        {shown.map(card => {
          const dates = card.start
            ? [formatDate(card.start, card.end ? card.start.getFullYear() !== card.end.getFullYear() : true), formatDate(card.end)].filter(Boolean).join(' – ')
            : '';
          const places = getPlaces(card);

          return (
            <li key={`${card.product}-${card.id}`} className={`${filterStyles.card} ${card.isExpired ? styles.expired : ''}`}>
              <div className={filterStyles.media}>
                {card.image ? (
                  <img src={card.image} alt="" loading="lazy" className={filterStyles.image} />
                ) : (
                  <div className={filterStyles.placeholder} aria-hidden="true">
                    <FontAwesomeIcon icon={PRODUCT_ICONS[card.product]} />
                  </div>
                )}
                <span className={filterStyles.type}>{intl.formatMessage({ id: PRODUCT_LABELS[card.product] })}</span>
              </div>

              <div className={filterStyles.body}>
                <div className={filterStyles.titleRow}>
                  <div className={filterStyles.titleBlock}>
                    <h3 className={filterStyles.name}>{getTitle(card)}</h3>
                    {[places, dates].filter(Boolean).length ? (
                      <p className={filterStyles.subtitle}>{[places, dates].filter(Boolean).join(' · ')}</p>
                    ) : null}
                  </div>
                  <div className={filterStyles.badges}>
                    {card.isExpired ? (
                      <span className={filterStyles.badge}>{intl.formatMessage({ id: 'my.account.hub.quotes.expired' })}</span>
                    ) : card.savePercent > 0 ? (
                      <span className={`${filterStyles.badge} ${filterStyles.badge_confirmed}`}>
                        {intl.formatMessage({ id: 'my.account.hub.certificates.teaser_save' }, { percent: card.savePercent })}
                      </span>
                    ) : null}
                  </div>
                </div>

                {card.comment ? (
                  <blockquote className={styles.note}>
                    <FontAwesomeIcon icon={faQuoteLeft} className={styles.noteIcon} aria-hidden="true" />
                    <div>
                      <span className={styles.noteLabel}>{intl.formatMessage({ id: 'my.account.hub.quotes.note_label' })}</span>
                      <p className={styles.noteText}>{card.comment}</p>
                    </div>
                  </blockquote>
                ) : null}

                <div className={filterStyles.footer}>
                  <div className={styles.actions}>
                    <button type="button" className={filterStyles.primaryButton} onClick={() => handleView(card)}>
                      {intl.formatMessage({ id: card.isExpired ? 'my.account.hub.quotes.view' : 'my.account.hub.quotes.view_reserve' })}
                    </button>
                    <a
                      href={`${import.meta.env.VITE_API_BFF_URL}/deals/quote/${encodeURIComponent(card.id)}/file`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.secondaryButton}
                      onClick={() => dispatch(markQuotesAsRead(card.id))}
                    >
                      {intl.formatMessage({ id: 'my.account.hub.quotes.download' })}
                    </a>
                  </div>
                  <div className={filterStyles.price}>
                    {card.created ? (
                      <span className={styles.shared}>{intl.formatMessage({ id: 'my.account.hub.quotes.shared_on' }, { date: formatDate(card.created) })}</span>
                    ) : null}
                    {card.fromPrice !== null ? (
                      <span className={styles.priceLine}>
                        {card.publicPrice ? <s className={styles.publicPrice}>{formatMoney(card.publicPrice, card.currency)}</s> : null}
                        <span className={filterStyles.total}>
                          {intl.formatMessage(
                            { id: card.options.length > 1 ? 'my.account.hub.quotes.from_price' : 'my.account.hub.quotes.price' },
                            { price: formatMoney(card.fromPrice, card.currency) },
                          )}
                        </span>
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className={filterStyles.bookings}>
      <div className={filterStyles.toolbar}>
        <div role="tablist" aria-label={intl.formatMessage({ id: 'my.account.hub.quotes.status_label' })} className={filterStyles.segmented}>
          {(['active', 'expired', 'all'] as StatusKey[]).map(key => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={status === key}
              className={status === key ? `${filterStyles.segment} ${filterStyles.segmentActive}` : filterStyles.segment}
              onClick={() => setStatus(key)}
            >
              {intl.formatMessage({ id: `my.account.hub.quotes.status.${key}` })}
            </button>
          ))}
        </div>
        <div className={filterStyles.controls}>
          <Select<SortKey>
            value={sort}
            onChange={setSort}
            className={filterStyles.sort}
            dropdownMatchSelectWidth={false}
            aria-label={intl.formatMessage({ id: 'my.account.hub.quotes.sort' })}
            options={[
              { value: 'newest', label: intl.formatMessage({ id: 'my.account.hub.quotes.sort_newest' }) },
              { value: 'soonest', label: intl.formatMessage({ id: 'my.account.hub.quotes.sort_soonest' }) },
            ]}
          />
        </div>
      </div>

      <div className={filterStyles.products} role="group" aria-label={intl.formatMessage({ id: 'my.account.hub.bookings.product_label' })}>
        <span className={filterStyles.productsLabel}>{intl.formatMessage({ id: 'my.account.hub.bookings.product_label' })}</span>
        {products.map(item => (
          <button
            key={item.key || 'all'}
            type="button"
            aria-pressed={product === item.key}
            className={product === item.key ? `${filterStyles.pill} ${filterStyles.pillActive}` : filterStyles.pill}
            onClick={() => setProduct(item.key)}
          >
            {intl.formatMessage({ id: item.labelId })}
          </button>
        ))}
      </div>

      {!loading && !errorHotels && cards.length ? (
        <p className={filterStyles.count}>{intl.formatMessage({ id: 'my.account.hub.quotes.count' }, { count: shown.length })}</p>
      ) : null}

      {renderList()}

      <QuotePackageModalCompare visible={!!openQuote} quoteBase={openQuote} onCancel={() => setOpenQuote(null)} />
    </div>
  );
};
